/**
 * useAutoSave Hook
 * Provides debounced auto-save functionality for evaluation forms.
 *
 * Concurrency model: at most ONE fetch in flight per hook instance. While a
 * save is in flight, the latest data lands in `queuedDataRef` (latest wins,
 * no deeper queue). On successful resolution the in-flight finally drains
 * the queue. On failure the queue is preserved but NOT auto-drained — the
 * next user edit (or the existing `retry` button) drives the next attempt,
 * which avoids retry storms during network outages.
 *
 * `saveImmediately` (used by Save & Finalize) bypasses the queue entirely:
 * it clears pending/queued slots, awaits any in-flight save, then fires its
 * own save and returns that promise — so the caller's `await` resolves only
 * after THIS save has actually landed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createLogger } from '@/utils/logger/devLogger';
import type { EvaluationData, EvaluationSection } from '@/utils/types/evaluation';

const logger = createLogger('useAutoSave');

/**
 * An evaluation that can be saved. Either a tryout registration (the legacy
 * tryout-day evaluation flow) or a standalone athlete evaluation created
 * from the athlete profile.
 */
export type AutoSaveEvaluationContext =
  | { source: 'tryout'; registrationId: string; organizationId?: string }
  | { source: 'standalone'; athleteEvaluationId: string };

interface UseAutoSaveOptions {
  context: AutoSaveEvaluationContext;
  section?: EvaluationSection;
  debounceMs?: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /** Requirements Summary (#743 — U4 / KTD7): seed the finalized lock at mount
   *  (e.g. from `status === 'finalized'`) so a finalized evaluation opens
   *  read-only — both the form wrapper and the new skill pickers — instead of
   *  staying interactive until the first save bounces with a 409. Defaults to
   *  false (no behavior change for tryout / draft callers). `clearFinalizedLocked`
   *  on unlock clears this same flag, so the form and pickers re-enable together. */
  initialFinalizedLocked?: boolean;
}

interface AutoSaveState {
  isSaving: boolean;
  lastSaved: Date | null;
  error: Error | null;
  /** True when the server rejected a save because the evaluation has been
   *  finalized (standalone only; 409 FINALIZED_LOCKED). While true, the hook
   *  skips auto-save entirely — no server traffic. The caller surfaces an
   *  inline "Unlock to edit" banner and calls `clearFinalizedLocked()` after
   *  a successful unlock to resume saves. */
  finalizedLocked: boolean;
  /** True when data is sitting in the serializer queue waiting for the
   *  current in-flight save to complete. Surfaced so the UI can render
   *  "Saving… (more pending)" instead of a misleading "Saved" flash. */
  hasQueued: boolean;
  /** True when there is keystroke-fresh data sitting in the debounce slot
   *  but no save has fired yet. Mirrors `pendingDataRef` / `debounceTimerRef`
   *  into reactive state so consumers (beforeunload, hasUnsavedWork) update
   *  immediately when the coach types instead of waiting for an unrelated
   *  re-render. Without this mirror the close-tab guard would silently miss
   *  the 1-second debounce window. */
  hasPendingDebounce: boolean;
}

export function useAutoSave(options: UseAutoSaveOptions) {
  const {
    context,
    section,
    debounceMs = 1000,
    onSuccess,
    onError,
    initialFinalizedLocked = false,
  } = options;

  const [state, setState] = useState<AutoSaveState>({
    isSaving: false,
    lastSaved: null,
    error: null,
    // Seeded from the row's finalized status at mount (#743 / KTD7) so the form
    // opens read-only; clearFinalizedLocked() resets it on unlock. The
    // finalizedLockedRef below initializes from this same value.
    finalizedLocked: initialFinalizedLocked,
    hasQueued: false,
    hasPendingDebounce: false,
  });

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDataRef = useRef<Partial<EvaluationData> | null>(null);
  const isMountedRef = useRef(true);

  // Concurrency-control refs. inFlightRef holds the promise of the current
  // save (or null). queuedDataRef holds the latest data to send next, if any
  // — replaced (not appended) by subsequent saves so only the freshest data
  // is sent. Refs (not state) so the serializer can read sync values without
  // waiting for React to re-render.
  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const queuedDataRef = useRef<Partial<EvaluationData> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true; // Reset to true on mount

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Drop any queued data so the in-flight finally doesn't fire a save
      // after unmount. The in-flight fetch itself is allowed to complete;
      // saveToServer no-ops state updates via isMountedRef.
      queuedDataRef.current = null;
      pendingDataRef.current = null;
    };
  }, []);

  // Ref mirror of state.finalizedLocked so the debounced save callback
  // doesn't capture a stale closure and keep scheduling saves after the
  // evaluation was locked.
  const finalizedLockedRef = useRef(state.finalizedLocked);
  useEffect(() => {
    finalizedLockedRef.current = state.finalizedLocked;
  }, [state.finalizedLocked]);

  /**
   * Save data to the server (raw fetch primitive — does NOT gate concurrency).
   * Always go through `runSerialized` from auto-save paths; only `saveImmediately`
   * may call this directly, after it has drained the in-flight slot.
   */
  const saveToServer = useCallback(
    async (data: Partial<EvaluationData>) => {
      if (!isMountedRef.current) return;

      setState(prev => ({ ...prev, isSaving: true, error: null }));

      try {
        logger.info('Auto-saving evaluation data:', {
          source: context.source,
          section,
          dataKeys: Object.keys(data),
        });

        let evalUrl: string;
        if (context.source === 'tryout') {
          evalUrl = context.organizationId
            ? `/api/tryout-registrations/${context.registrationId}/evaluate?organizationId=${context.organizationId}`
            : `/api/tryout-registrations/${context.registrationId}/evaluate`;
        } else {
          evalUrl = `/api/athlete-evaluations/${context.athleteEvaluationId}/evaluate`;
        }
        const response = await fetch(
          evalUrl,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              section,
              data,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // FINALIZED_LOCKED is distinct from a transient save failure — the
          // evaluation is intentionally locked. Flip the flag and stop
          // touching the server; the UI surfaces an inline unlock banner.
          if (response.status === 409 && errorData?.code === 'FINALIZED_LOCKED') {
            // Set ref synchronously so the serializer's .finally sees the
            // lock without waiting for React to commit the setState below.
            // Also drop any queued data — it would 409 too.
            finalizedLockedRef.current = true;
            queuedDataRef.current = null;
            if (isMountedRef.current) {
              setState((prev) => ({
                ...prev,
                isSaving: false,
                finalizedLocked: true,
                hasQueued: false,
                error: null,
              }));
            }
            return;
          }
          throw new Error(errorData?.error || 'Failed to save evaluation');
        }

        const result = await response.json();

        if (!isMountedRef.current) return;

        setState((prev) => ({
          ...prev,
          isSaving: false,
          lastSaved: new Date(),
          error: null,
        }));

        logger.info('Auto-save successful:', {
          completionPercentage: result.evaluation_completion_percentage,
        });

        if (onSuccess) {
          onSuccess();
        }

        return result;
      } catch (error) {
        logger.error('Auto-save failed:', error);

        if (!isMountedRef.current) return;

        const err = error instanceof Error ? error : new Error('Unknown error');

        setState(prev => ({
          ...prev,
          isSaving: false,
          error: err,
        }));

        if (onError) {
          onError(err);
        }

        throw error;
      }
    },
    [context, section, onSuccess, onError]
  );

  // Latest-saveToServer ref. saveToServer's `useCallback` deps include
  // `section`, so it rebuilds when the coach switches tabs mid-save. The
  // serializer's drain `.finally` would otherwise capture the OLD closure
  // and POST the stale section to the server's audit log on the queued
  // save. Read through the ref so the next drain always uses the freshest
  // section value.
  const latestSaveToServerRef = useRef(saveToServer);
  useEffect(() => {
    latestSaveToServerRef.current = saveToServer;
  }, [saveToServer]);

  /**
   * Serializer wrapper. Guarantees at most one fetch in flight; queues the
   * latest data while a save is running. On success drains the queue; on
   * failure leaves the queue alone (caller's next save() or retry() drives it).
   */
  const runSerialized = useCallback(
    (data: Partial<EvaluationData>): Promise<unknown> => {
      if (inFlightRef.current) {
        queuedDataRef.current = data;
        if (isMountedRef.current) {
          setState((prev) => (prev.hasQueued ? prev : { ...prev, hasQueued: true }));
        }
        return inFlightRef.current;
      }

      // Capture success/failure since .finally doesn't see the outcome.
      let succeeded = false;
      // Always use the LATEST saveToServer so the section value reflects the
      // tab the coach is currently on, not whichever tab was active when this
      // runSerialized closure was first created.
      const p = latestSaveToServerRef.current(data)
        .then((result) => {
          succeeded = true;
          return result;
        })
        .catch((err) => {
          succeeded = false;
          // Re-throw so saveImmediately's await can see the rejection if it
          // is currently waiting on this promise. (Auto-save paths swallow
          // it via runSerialized's outer .catch below.)
          throw err;
        })
        .finally(() => {
          inFlightRef.current = null;
          // Drain only on success — on failure leave the queue for the next
          // user edit / retry click. If the lock fired, queue was cleared in
          // saveToServer's 409 handler.
          if (
            succeeded &&
            queuedDataRef.current !== null &&
            isMountedRef.current &&
            !finalizedLockedRef.current
          ) {
            const next = queuedDataRef.current;
            queuedDataRef.current = null;
            // Clear hasQueued NOW — the queue is empty until the user types
            // again. The new in-flight cycle keeps isSaving true, so the
            // indicator stays "Saving..." (not the misleading "more pending").
            if (isMountedRef.current) {
              setState((prev) =>
                prev.hasQueued ? { ...prev, hasQueued: false } : prev
              );
            }
            // Tail-call: this re-enters runSerialized, which sees inFlightRef
            // is null and starts a fresh fetch.
            runSerialized(next);
          } else if (isMountedRef.current) {
            setState((prev) => (prev.hasQueued ? { ...prev, hasQueued: false } : prev));
          }
        });

      inFlightRef.current = p;
      // Swallow rejections on the outer promise so unhandled-promise warnings
      // don't fire for auto-save paths. Callers that care (saveImmediately)
      // hold their own reference to the underlying saveToServer promise.
      p.catch(() => {});
      return p;
    },
    [saveToServer]
  );

  /**
   * Trigger auto-save with debouncing
   */
  const save = useCallback(
    (data: Partial<EvaluationData>) => {
      // If the evaluation is locked, silently drop the save. The caller
      // shows a banner and calls clearFinalizedLocked() after unlock.
      if (finalizedLockedRef.current) return;

      // Store the latest data
      pendingDataRef.current = data;

      // Mirror into state so beforeunload / hasUnsavedWork are reactive.
      // Refs alone don't trigger a re-render, which would leave the
      // close-tab guard silently un-attached during the debounce window.
      if (isMountedRef.current) {
        setState((prev) =>
          prev.hasPendingDebounce ? prev : { ...prev, hasPendingDebounce: true }
        );
      }

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        // Only check if we have pending data (remove isMounted check for React 18 Strict Mode compatibility)
        if (pendingDataRef.current && !finalizedLockedRef.current) {
          const next = pendingDataRef.current;
          pendingDataRef.current = null;
          if (isMountedRef.current) {
            setState((prev) =>
              prev.hasPendingDebounce ? { ...prev, hasPendingDebounce: false } : prev
            );
          }
          runSerialized(next);
        } else {
          if (isMountedRef.current) {
            setState((prev) =>
              prev.hasPendingDebounce ? { ...prev, hasPendingDebounce: false } : prev
            );
          }
        }
      }, debounceMs);
    },
    [debounceMs, runSerialized]
  );

  /** Clears the finalizedLocked flag after a successful unlock so saves
   *  resume. Called by EvaluationTabLayout from its Unlock handler. */
  const clearFinalizedLocked = useCallback(() => {
    finalizedLockedRef.current = false;
    setState((prev) => ({ ...prev, finalizedLocked: false }));
  }, []);

  /**
   * Save immediately without debouncing. Used by "Save & Finalize" — the
   * returned promise resolves only after THIS save has landed, even if other
   * saves were in flight or queued when the call was made.
   *
   * Note: on slow networks this may take longer than the debounce since it
   * drains in-flight saves first. The two-stage UI label in
   * EvaluationTabLayout's SaveAndFinalizeButton communicates this to coaches.
   */
  const saveImmediately = useCallback(
    async (data: Partial<EvaluationData>) => {
      // Finalized evaluations never save — mirror the guard in save() so the
      // "no write while finalized" invariant lives in the hook, not just in
      // each caller's render-gating (#743 — U4/KTD7 hardening). The Save &
      // Finalize button is already hidden while finalizedLocked, so this is
      // defense-in-depth for any future saveImmediately caller.
      if (finalizedLockedRef.current) return;
      // Clear any pending debounced save and any queued data — this call's
      // `data` is the freshest snapshot the caller has, so older slots are
      // either already represented in `data` or no longer wanted.
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingDataRef.current = null;
      queuedDataRef.current = null;
      if (isMountedRef.current) {
        setState((prev) =>
          prev.hasQueued || prev.hasPendingDebounce
            ? { ...prev, hasQueued: false, hasPendingDebounce: false }
            : prev
        );
      }

      // Reserve the in-flight slot SYNCHRONOUSLY with a placeholder before
      // we await anything. Two concurrent saveImmediately callers (e.g., a
      // future "Save now" button next to Finalize) would otherwise both see
      // null between the await-loop exit and the assignment, and both fire
      // parallel fetches — the exact bug we're fixing.
      const previousInFlight = inFlightRef.current;
      let resolvePlaceholder!: () => void;
      const placeholder = new Promise<void>((r) => {
        resolvePlaceholder = r;
      });
      inFlightRef.current = placeholder;
      placeholder.catch(() => {});

      try {
        // Drain any prior in-flight save. (If a queued save was waiting on
        // its .finally, it will see our placeholder and queue behind us.)
        if (previousInFlight) {
          try {
            await previousInFlight;
          } catch {
            // Prior save's failure already surfaced via state.error / onError.
          }
        }

        // Fire fresh — same latest-ref pattern as runSerialized so the
        // section reflects the tab the coach is currently on.
        const real = latestSaveToServerRef.current(data);
        // Swap the placeholder for the real promise so subsequent saves
        // await this fetch (not just the placeholder). Identity-check on
        // clear so a later save's wrapper isn't stomped to null by ours.
        const wrapper: Promise<unknown> = real.finally(() => {
          if (inFlightRef.current === wrapper) {
            inFlightRef.current = null;
          }
        });
        inFlightRef.current = wrapper;
        wrapper.catch(() => {});
        return await real;
      } finally {
        // Release the placeholder in case anything was awaiting it before
        // the swap.
        resolvePlaceholder();
      }
    },
    [saveToServer]
  );

  /**
   * Retry last failed save
   */
  const retry = useCallback(() => {
    // Prefer queued data (most recent) over pendingDataRef.
    const data = queuedDataRef.current ?? pendingDataRef.current;
    if (!data) return;
    // Clear BOTH slots before re-firing — otherwise a second retry click
    // would resend the same data, and the in-flight save's success path
    // would also drain its own copy. Both paths consume `data` exactly once.
    queuedDataRef.current = null;
    pendingDataRef.current = null;
    if (isMountedRef.current) {
      setState((prev) =>
        prev.hasQueued || prev.hasPendingDebounce
          ? { ...prev, hasQueued: false, hasPendingDebounce: false }
          : prev
      );
    }
    runSerialized(data);
  }, [runSerialized]);

  // hasUnsavedWork: anything from "user typed in the last second" through
  // "save in flight" through "queued behind in-flight". Used by the
  // EvaluationTabLayout beforeunload guard. Composed entirely of state
  // values (not refs) so React re-renders the consumer the moment any of
  // these flips — the close-tab guard wires/unwires synchronously with
  // the underlying state.
  const hasUnsavedWork =
    state.isSaving || state.hasQueued || state.hasPendingDebounce;

  return {
    save,
    saveImmediately,
    retry,
    clearFinalizedLocked,
    isSaving: state.isSaving,
    lastSaved: state.lastSaved,
    error: state.error,
    finalizedLocked: state.finalizedLocked,
    hasQueued: state.hasQueued,
    hasUnsavedWork,
  };
}
